import React, { useState } from "react";
import { PageHeader } from "../components/layout/Shell";
import StatutoryShell from "../components/statutory/StatutoryShell";
import { StatutoryProvider, useStatutory } from "../components/statutory/StatutoryContext";
import { StatutoryBadge, StatutoryStatePanel } from "../components/statutory/StatutoryBadge";
import { evidenceRecords } from "../mock/statutory/mockData";
import { FileText, ChevronRight, ExternalLink, X } from "lucide-react";
import { toast } from "sonner";

function EvidenceDetailDrawer({ record, onClose }) {
  if (!record) return null;
  const isBlocked = !!record.blockingOQ;

  return (
    <div
      className="fixed inset-y-0 right-0 w-full md:w-[480px] bg-background border-l border-border shadow-xl z-50 flex flex-col overflow-y-auto"
      data-testid="evidence-detail-drawer"
    >
      <div className="flex items-center justify-between p-4 border-b border-border sticky top-0 bg-background z-10">
        <div>
          <div className="font-semibold text-sm">{record.instrumentTitle}</div>
          <div className="text-xs text-muted-foreground">{record.id}</div>
        </div>
        <button onClick={onClose} className="p-1.5 hover:bg-muted rounded" data-testid="close-drawer">
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="p-4 space-y-4 flex-1">
        {isBlocked && (
          <StatutoryStatePanel
            state={record.interpretationStatus}
            reason={record.notes}
            oqRef={record.blockingOQ}
          />
        )}

        <div className="rounded-lg border border-border p-4 space-y-3">
          <div className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Citation Details</div>
          {[
            ["Instrument", record.instrumentTitle],
            ["Article / Section", record.articleSection],
            ["Source Authority", record.sourceAuthority],
            ["Source Type", record.sourceType],
            ["Jurisdiction", record.jurisdiction],
            ["Regime", record.regime],
            ["Publication Date", record.publicationDate || "—"],
            ["Verified By", record.verifiedBy || "—"],
            ["Verified At", record.verifiedAt || "—"],
          ].map(([k, v]) => (
            <div key={k} className="flex items-start justify-between gap-3">
              <dt className="text-xs text-muted-foreground shrink-0">{k}</dt>
              <dd className="text-xs font-mono text-foreground text-right">{v}</dd>
            </div>
          ))}
        </div>

        <div className="rounded-lg border border-border p-4 space-y-2">
          <div className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Status</div>
          <div className="flex items-center gap-2 flex-wrap">
            <StatutoryBadge state={record.sourceStatus} />
            <StatutoryBadge state={record.verificationStatus === "VERIFIED" ? "APPROVED" : "PENDING"} />
            <StatutoryBadge state={record.interpretationStatus} />
            <StatutoryBadge state={record.counselStatus} />
          </div>
        </div>

        {record.notes && (
          <div className="rounded-lg border border-border p-4">
            <div className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2">Interpretation Notes</div>
            <p className="text-sm text-foreground/80">{record.notes}</p>
          </div>
        )}

        <div className="rounded-lg border border-border p-4">
          <div className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2">Linked Rule Families</div>
          {record.linkedRuleVersions.length > 0
            ? record.linkedRuleVersions.map((id) => (
              <span key={id} className="inline-block text-xs font-mono bg-muted text-foreground rounded px-2 py-0.5 mr-1 mb-1">{id}</span>
            ))
            : <p className="text-xs text-muted-foreground">No active rule versions linked — evidence blocked or unverified.</p>
          }
        </div>

        <div className="text-[11px] text-muted-foreground italic border-t border-border pt-3">
          Immutable fields cannot be modified. Corrections require a new evidence record with supersedes_evidence_id.
        </div>
      </div>
    </div>
  );
}

function EvidenceContent() {
  const { currentRole } = useStatutory();
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState("ALL");

  const filters = ["ALL", "PRIMARY_SOURCE_VERIFIED", "SOURCE_IDENTIFIED", "COUNSEL_REVIEW_REQUIRED"];
  const filtered = filter === "ALL" ? evidenceRecords : evidenceRecords.filter((r) => r.sourceStatus === filter || r.interpretationStatus === filter || r.counselStatus === filter);

  return (
    <div className="relative">
      {/* Filters */}
      <div className="flex gap-2 mb-4 flex-wrap">
        {filters.map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`text-xs px-2.5 py-1 rounded border transition-colors ${filter === f ? "border-primary bg-primary/10 text-primary font-medium" : "border-border text-muted-foreground hover:text-foreground"}`}
            data-testid={`filter-${f}`}
          >
            {f.replace(/_/g, " ")}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-xs" data-testid="evidence-table">
          <thead className="bg-muted/40">
            <tr>
              {["ID", "Instrument", "Jurisdiction", "Regime", "Source Status", "Interpretation", "Counsel", "Verified By", "Actions"].map((h) => (
                <th key={h} className="text-left px-3 py-2.5 font-medium text-muted-foreground whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {filtered.map((rec) => (
              <tr
                key={rec.id}
                className={`hover:bg-muted/30 transition-colors ${rec.blockingOQ ? "bg-amber-50/30" : ""}`}
                data-testid={`evidence-row-${rec.id}`}
              >
                <td className="px-3 py-2.5 font-mono text-muted-foreground">{rec.id}</td>
                <td className="px-3 py-2.5 max-w-[200px]">
                  <div className="font-medium text-foreground truncate">{rec.instrumentTitle}</div>
                  <div className="text-muted-foreground truncate">{rec.articleSection}</div>
                </td>
                <td className="px-3 py-2.5 font-mono">{rec.jurisdiction}</td>
                <td className="px-3 py-2.5 font-mono">{rec.regime}</td>
                <td className="px-3 py-2.5"><StatutoryBadge state={rec.sourceStatus} /></td>
                <td className="px-3 py-2.5"><StatutoryBadge state={rec.interpretationStatus} /></td>
                <td className="px-3 py-2.5"><StatutoryBadge state={rec.counselStatus} /></td>
                <td className="px-3 py-2.5 text-muted-foreground">{rec.verifiedBy || "—"}</td>
                <td className="px-3 py-2.5">
                  <button
                    onClick={() => setSelected(rec)}
                    className="text-xs text-primary hover:underline"
                    data-testid={`view-evidence-${rec.id}`}
                  >
                    View
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {currentRole.canWrite && (
        <div className="mt-4 flex gap-2">
          <button
            onClick={() => toast.info("Prototype: Create evidence record — no backend.")}
            className="text-xs border border-border rounded px-3 py-1.5 hover:bg-muted flex items-center gap-1.5"
            data-testid="create-evidence-btn"
          >
            <FileText className="h-3 w-3" />
            Add Evidence Record
          </button>
          <p className="text-[11px] text-muted-foreground self-center">Prototype action only.</p>
        </div>
      )}

      {selected && <EvidenceDetailDrawer record={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

export default function StatutoryEvidence() {
  return (
    <StatutoryProvider>
      <PageHeader eyebrow="Statutory Configuration" title="Legal Evidence" description="Statutory evidence references and verification status — mock prototype." />
      <StatutoryShell>
        <EvidenceContent />
      </StatutoryShell>
    </StatutoryProvider>
  );
}
